import type { TrainingBlockExercise } from '../../../types/training';

export const clusterContainsExerciseId = (
  item: TrainingBlockExercise,
  exerciseId: string,
): boolean => {
  if (item.id === exerciseId) {
    return true;
  }

  return (item.conjugatedExercises ?? []).some((child) => clusterContainsExerciseId(child, exerciseId));
};

export const extractExerciseFromTree = (
  items: TrainingBlockExercise[],
  exerciseId: string,
): { nextItems: TrainingBlockExercise[]; extractedExercise: TrainingBlockExercise | null } => {
  let extractedExercise: TrainingBlockExercise | null = null;

  const nextItems = items.flatMap((item) => {
    if (item.id === exerciseId) {
      extractedExercise = {
        ...item,
        conjugatedExercises: [],
      };
      const remainingGroupedItems = item.conjugatedExercises ?? [];

      if (remainingGroupedItems.length === 0) {
        return [];
      }

      const [nextGroupParent, ...groupedSiblings] = remainingGroupedItems;

      return [{
        ...nextGroupParent,
        conjugatedExercises: [
          ...(nextGroupParent.conjugatedExercises ?? []),
          ...groupedSiblings,
        ],
      }];
    }

    const childExtraction = extractExerciseFromTree(item.conjugatedExercises ?? [], exerciseId);

    if (!childExtraction.extractedExercise) {
      return [item];
    }

    extractedExercise = childExtraction.extractedExercise;
    return [{
      ...item,
      conjugatedExercises: childExtraction.nextItems,
    }];
  });

  return { nextItems, extractedExercise };
};

export const flattenExerciseCluster = (exercise: TrainingBlockExercise): TrainingBlockExercise[] => {
  const nestedExercises = exercise.conjugatedExercises ?? [];

  return [
    {
      ...exercise,
      conjugatedExercises: [],
    },
    ...nestedExercises.flatMap((nestedExercise) => flattenExerciseCluster(nestedExercise)),
  ];
};

const collectIds = (items: TrainingBlockExercise[]): string[] => items.flatMap((item) => [
  item.id,
  ...collectIds(item.conjugatedExercises ?? []),
]);

const haveSameOrder = (left: TrainingBlockExercise[], right: TrainingBlockExercise[]) => {
  const leftIds = collectIds(left);
  const rightIds = collectIds(right);

  return leftIds.length === rightIds.length && leftIds.every((id, index) => id === rightIds[index]);
};

export const reorderExercisesInSameCluster = (
  items: TrainingBlockExercise[],
  sourceId: string,
  targetId: string,
  placement: 'before' | 'after',
): TrainingBlockExercise[] | null => {
  if (sourceId === targetId) {
    return items;
  }

  const clusterIndex = items.findIndex((item) => (
    clusterContainsExerciseId(item, sourceId) && clusterContainsExerciseId(item, targetId)
  ));

  if (clusterIndex < 0) {
    return null;
  }

  const cluster = items[clusterIndex];
  const sequence = flattenExerciseCluster(cluster);
  const sourceIndex = sequence.findIndex((exercise) => exercise.id === sourceId);
  const targetIndex = sequence.findIndex((exercise) => exercise.id === targetId);

  if (sourceIndex < 0 || targetIndex < 0) {
    return null;
  }

  const nextSequence = [...sequence];
  const [movedExercise] = nextSequence.splice(sourceIndex, 1);
  const nextTargetIndex = nextSequence.findIndex((exercise) => exercise.id === targetId);
  const insertIndex = placement === 'after' ? nextTargetIndex + 1 : nextTargetIndex;
  nextSequence.splice(insertIndex, 0, movedExercise);

  if (nextSequence.every((exercise, index) => exercise.id === sequence[index].id)) {
    return items;
  }

  const [nextParent, ...nextChildren] = nextSequence;

  return items.map((item, index) => {
    if (index !== clusterIndex) {
      return item;
    }

    return {
      ...nextParent,
      groupingType: cluster.groupingType,
      conjugatedExercises: nextChildren.map((child) => ({
        ...child,
        groupingType: cluster.groupingType,
        conjugatedExercises: [],
      })),
    };
  });
};

export const CATALOG_INSERT_PREVIEW_ID = '__catalog-insert-preview__';

export const previewInsertAtPosition = (
  items: TrainingBlockExercise[],
  previewItem: TrainingBlockExercise,
  targetId: string,
  placement: 'before' | 'after',
): TrainingBlockExercise[] | null => {
  const targetIndex = items.findIndex((item) => (
    item.id === targetId || clusterContainsExerciseId(item, targetId)
  ));

  if (targetIndex < 0) {
    return null;
  }

  const nextItems = items.filter((item) => item.id !== previewItem.id);
  const nextTargetIndex = nextItems.findIndex((item) => (
    item.id === targetId || clusterContainsExerciseId(item, targetId)
  ));

  if (nextTargetIndex < 0) {
    return null;
  }

  const insertIndex = placement === 'after' ? nextTargetIndex + 1 : nextTargetIndex;
  nextItems.splice(insertIndex, 0, previewItem);
  return nextItems;
};

export const previewReorderItems = (
  items: TrainingBlockExercise[],
  sourceId: string,
  targetId: string,
  placement: 'before' | 'after',
): TrainingBlockExercise[] | null => {
  if (sourceId === targetId) {
    return null;
  }

  const reorderedCluster = reorderExercisesInSameCluster(items, sourceId, targetId, placement);

  if (reorderedCluster) {
    return haveSameOrder(items, reorderedCluster) ? null : reorderedCluster;
  }

  const sourceIndex = items.findIndex((item) => item.id === sourceId);

  if (sourceIndex < 0) {
    return null;
  }

  const targetIndex = items.findIndex((item) => (
    item.id === targetId || clusterContainsExerciseId(item, targetId)
  ));

  if (targetIndex < 0) {
    return null;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(sourceIndex, 1);
  const nextTargetIndex = nextItems.findIndex((item) => (
    item.id === targetId || clusterContainsExerciseId(item, targetId)
  ));

  if (nextTargetIndex < 0) {
    return null;
  }

  const insertIndex = placement === 'after' ? nextTargetIndex + 1 : nextTargetIndex;
  nextItems.splice(insertIndex, 0, movedItem);

  return haveSameOrder(items, nextItems) ? null : nextItems;
};
